import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import {
  decalageSocial,
  fenetre,
  frise,
  parJourSemaine,
  resumer,
  serie,
} from '../../analysis/statistiques'
import { db } from '../../db/db'
import { lireReglages } from '../../db/repo'
import { formatDuree, formatEcart, formatJourCourt } from '../../domain/time'
import { Bascule, Carte, Encart, Entete } from '../composants'
import { BarresDuree, BarresHorizontales, Figure, FriseNuits } from '../Graphiques'

type Periode = '7' | '30' | '90'

const PERIODES: { valeur: Periode; libelle: string }[] = [
  { valeur: '7', libelle: '7 j' },
  { valeur: '30', libelle: '30 j' },
  { valeur: '90', libelle: '90 j' },
]

export function PageStatistiques() {
  const [periode, setPeriode] = useState<Periode>('30')

  const donnees = useLiveQuery(async () => {
    const nuits = await db.nuits.toArray()
    return { nuits, reglages: await lireReglages() }
  }, [])

  // Le calcul est pur : il est memoise sur les donnees et la periode, jamais
  // refait a chaque rendu.
  const calcul = useMemo(() => {
    if (!donnees) return null
    const jours = Number(periode)
    const points = serie(donnees.nuits, jours)
    const dedans = fenetre(donnees.nuits, jours)
    return {
      points,
      dedans,
      resume: resumer(dedans, donnees.reglages.objectifMinutes),
      barres: frise(points),
      semaine: parJourSemaine(dedans),
      decalage: decalageSocial(dedans),
    }
  }, [donnees, periode])

  if (!donnees || !calcul) {
    return (
      <>
        <Entete titre="Statistiques" />
        <main className="contenu">
          <p className="doux">Chargement…</p>
        </main>
      </>
    )
  }

  const { resume, points, barres, semaine, decalage } = calcul
  const objectifMs = donnees.reglages.objectifMinutes * 60_000

  if (resume.nombre === 0) {
    return (
      <>
        <Entete titre="Statistiques" />
        <main className="contenu">
          <Bascule valeur={periode} options={PERIODES} onChange={setPeriode} etiquette="Période" />
          <Carte>
            <p className="doux">Aucune nuit sur cette période.</p>
          </Carte>
        </main>
      </>
    )
  }

  const maxSemaine = Math.max(...semaine.map((l) => l.moyenneMs ?? 0), objectifMs)

  return (
    <>
      <Entete
        titre="Statistiques"
        sous={`${resume.nombre} nuit${resume.nombre > 1 ? 's' : ''} sur ${periode} jours`}
      />

      <main className="contenu">
        <Bascule valeur={periode} options={PERIODES} onChange={setPeriode} etiquette="Période" />

        <Carte>
          <div className="mesures">
            <div className="mesure">
              <div className="mesure__valeur">
                {resume.moyenneMs === null ? '—' : formatDuree(resume.moyenneMs)}
              </div>
              <div className="mesure__libelle">moyenne</div>
            </div>
            <div className="mesure">
              <div className="mesure__valeur">
                {resume.medianeMs === null ? '—' : formatDuree(resume.medianeMs)}
              </div>
              <div className="mesure__libelle">médiane</div>
            </div>
            <div className="mesure">
              <div className="mesure__valeur">
                {resume.efficaciteMoyenne === null
                  ? '—'
                  : `${Math.round(resume.efficaciteMoyenne * 100)} %`}
              </div>
              <div className="mesure__libelle">efficacité</div>
            </div>
          </div>
        </Carte>

        <Carte>
          <Figure
            titre="Frise des nuits"
            legende={
              <p className="tres-doux">
                Chaque ligne est une nuit, de 18 h à 14 h. Les segments orangés sont les
                réveils décomptés.
              </p>
            }
            tableau={
              <table>
                <thead>
                  <tr>
                    <th>Nuit</th>
                    <th className="nombre">Posé</th>
                    <th className="nombre">Réveils</th>
                  </tr>
                </thead>
                <tbody>
                  {points
                    .filter((p) => p.mesures)
                    .map((p) => (
                      <tr key={p.date}>
                        <td>{formatJourCourt(p.date)}</td>
                        <td className="nombre">{formatDuree(p.mesures?.tempsPoseMs ?? 0)}</td>
                        <td className="nombre">{p.mesures?.reveils ?? 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            }
          >
            <FriseNuits barres={barres} />
          </Figure>
        </Carte>

        <Carte>
          <Figure
            titre="Temps posé par nuit"
            legende={
              <p className="tres-doux">
                La ligne marque l’objectif de {formatDuree(objectifMs)}. Un jour sans barre
                est un jour sans mesure, pas une nuit blanche.
              </p>
            }
            tableau={
              <table>
                <thead>
                  <tr>
                    <th>Nuit</th>
                    <th className="nombre">Posé</th>
                  </tr>
                </thead>
                <tbody>
                  {points.map((p) => (
                    <tr key={p.date}>
                      <td>{formatJourCourt(p.date)}</td>
                      <td className="nombre">
                        {p.mesures ? formatDuree(p.mesures.tempsPoseMs) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <BarresDuree
              points={points.map((p) => ({
                libelle: formatJourCourt(p.date).replace(/\.$/, ''),
                valeur: p.mesures ? p.mesures.tempsPoseMs : null,
                info: p.mesures
                  ? `${formatJourCourt(p.date)} : ${formatDuree(p.mesures.tempsPoseMs)}`
                  : `${formatJourCourt(p.date)} : pas de mesure`,
              }))}
              objectifMs={objectifMs}
            />
          </Figure>
        </Carte>

        <Carte titre="Régularité">
          <div className="pile pile--serree">
            <div className="ligne ligne--espacee">
              <span className="doux">Coucher moyen</span>
              <span className="mono gras">
                {resume.coucherMoyen ?? '—'}
                {resume.regulariteCoucherMin !== null && (
                  <span className="tres-doux"> ± {Math.round(resume.regulariteCoucherMin)} min</span>
                )}
              </span>
            </div>
            <div className="ligne ligne--espacee">
              <span className="doux">Lever moyen</span>
              <span className="mono gras">
                {resume.leverMoyen ?? '—'}
                {resume.regulariteLeverMin !== null && (
                  <span className="tres-doux"> ± {Math.round(resume.regulariteLeverMin)} min</span>
                )}
              </span>
            </div>
            <hr className="separateur" />
            <p className="tres-doux">
              L’écart type dit à quel point les nuits se ressemblent. C’est la mesure la
              plus actionnable du lot : à durée égale, des horaires stables valent mieux
              que des horaires flottants.
            </p>
          </div>
        </Carte>

        <Carte>
          <Figure
            titre="Par jour de la semaine"
            tableau={
              <table>
                <thead>
                  <tr>
                    <th>Jour</th>
                    <th className="nombre">Moyenne</th>
                    <th className="nombre">Nuits</th>
                  </tr>
                </thead>
                <tbody>
                  {semaine.map((l) => (
                    <tr key={l.jour}>
                      <td>{l.jour}</td>
                      <td className="nombre">
                        {l.moyenneMs === null ? '—' : formatDuree(l.moyenneMs)}
                      </td>
                      <td className="nombre">{l.nombre}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <BarresHorizontales
              lignes={semaine.map((l) => ({
                libelle: l.jour,
                valeur: l.moyenneMs,
                detail: l.nombre > 0 ? `${l.nombre} nuit${l.nombre > 1 ? 's' : ''}` : undefined,
              }))}
              max={maxSemaine}
            />
          </Figure>
        </Carte>

        <Carte titre="Dette et décalage">
          <div className="pile pile--serree">
            <div className="ligne ligne--espacee">
              <span className="doux">Dette cumulée</span>
              <span className="mono gras">{formatEcart(-resume.detteMs)}</span>
            </div>
            <div className="ligne ligne--espacee">
              <span className="doux">Décalage social</span>
              <span className="mono gras">
                {decalage === null ? '—' : formatDuree(decalage)}
              </span>
            </div>
            <hr className="separateur" />
            <p className="tres-doux">
              Le décalage social compare le milieu de nuit des week-ends à celui de la
              semaine. Deux heures d’écart reviennent à changer de fuseau horaire chaque
              vendredi. Il reste vide tant qu’il manque des nuits d’un côté ou de l’autre.
            </p>
          </div>
        </Carte>

        <Encart ton="info">
          Toutes ces durées mesurent un <strong>téléphone posé</strong>, jamais un sommeil.
          Un réveil sans toucher au téléphone est invisible : les réveils comptés sont un
          plancher.
        </Encart>
      </main>
    </>
  )
}
